import { flattenDeep } from 'lodash'

import { ROOT_CATALOG_ID } from '@marketplace-web/catalog/catalog-data'

import { ItemFacetCatalogDto } from '../types/dtos/item-facets'

import { CatalogPhotoDto } from '../types/dtos/catalog'
import { WardrobeCatalogModel, WardrobeCatalogWithParentIdModel } from '../types/models/catalog'
import { WardrobeCatalogDto } from '../types/dtos/get-wardrobe-catalogs-resp'

function isCatalogPhotoDto(
  value: Record<string, unknown> | undefined | null,
): value is CatalogPhotoDto {
  return value != null && 'url' in value
}

export const transformWardrobeCatalogDto = (catalog: WardrobeCatalogDto): WardrobeCatalogModel => {
  const { id, title, code, photo } = catalog

  return {
    id,
    title,
    code,
    photo: isCatalogPhotoDto(photo) ? photo : null,
    catalogIds: catalog.catalogs.map(item => item.id),
    depth: 0,
    itemCount: catalog.item_count || 0,
    catalogs: catalog.catalogs.map(dto => transformWardrobeCatalogDto(dto)),
  }
}

const wardrobeCatalogTree = (
  catalogs: Array<WardrobeCatalogDto>,
  depth = 0,
): RecursiveArray<WardrobeCatalogDto> =>
  catalogs.map(catalog => {
    const catalogWithDepth = {
      ...catalog,
      depth,
    }

    return [catalogWithDepth, ...wardrobeCatalogTree(catalog.catalogs, depth + 1)]
  })

const flattenWardrobeCatalogDtos = (
  catalogs: Array<WardrobeCatalogDto>,
): Array<WardrobeCatalogDto> => flattenDeep(wardrobeCatalogTree(catalogs))

const applyParentIdToWardrobe = (
  catalog: WardrobeCatalogModel,
  index: number,
  catalogs: Array<WardrobeCatalogModel>,
): WardrobeCatalogWithParentIdModel => {
  const parent = catalogs.find(it => it.catalogIds.includes(catalog.id))
  const parentId = parent ? parent.id : ROOT_CATALOG_ID

  return {
    ...catalog,
    parentId,
    catalogs: catalog.catalogs.map((childCatalog, childIndex, childCatalogs) =>
      applyParentIdToWardrobe(childCatalog, childIndex, childCatalogs),
    ),
  }
}

export const transformWardrobeCatalogDtos = (
  catalogs: Array<WardrobeCatalogDto>,
): Array<WardrobeCatalogWithParentIdModel> =>
  flattenWardrobeCatalogDtos(catalogs).map(transformWardrobeCatalogDto).map(applyParentIdToWardrobe)

export const getWardrobeCatalogByCounts = (
  catalogs: Array<WardrobeCatalogDto>,
  catalogCounts: Record<number, number>,
) =>
  catalogs.reduce(
    (accumulator: { catalogs: Array<WardrobeCatalogDto>; count: number }, catalog) => {
      let filteredChildren: Array<WardrobeCatalogDto> = []
      let childrenItemSum = 0

      if (catalog.catalogs.length) {
        const childrenCounts = getWardrobeCatalogByCounts(catalog.catalogs, catalogCounts)

        filteredChildren = childrenCounts.catalogs
        childrenItemSum = childrenCounts.count
      }

      if (catalogCounts[catalog.id] || filteredChildren.length) {
        const itemCount = childrenItemSum + (catalogCounts[catalog.id] || 0)

        accumulator.catalogs.push({
          ...catalog,
          catalogs: filteredChildren,
          item_count: itemCount,
        })
        accumulator.count += itemCount
      }

      return accumulator
    },
    { catalogs: [], count: 0 },
  )

export const transformWardrobeCatalogDtosWithCounts = (
  catalogs: Array<WardrobeCatalogDto>,
  catalogCounts: Array<ItemFacetCatalogDto>,
): Array<WardrobeCatalogWithParentIdModel> => {
  const counts = catalogCounts.reduce(
    (acc: Record<number, number>, { id, count }) => ({ ...acc, [id]: count }),
    {},
  )
  const catalogsWithCounts = getWardrobeCatalogByCounts(catalogs, counts).catalogs

  return transformWardrobeCatalogDtos(catalogsWithCounts)
}
